import { ReactNode, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Zap, Image, BarChart3 } from "lucide-react";

type AuthPageLayoutProps = {
  children: ReactNode;
  twoColumn: boolean;
  valueProps?: ReactNode;
};

export function AuthPageLayout({
  children,
  valueProps,
  twoColumn,
}: AuthPageLayoutProps) {
  const location = useLocation();

  const transformImages = "Transform Your Product Images";
  const joinMerchants =
    "Join 2,500+ merchants using AI-generated images that convert";
  const aiImageGenDesc =
    "Create professional product images from simple descriptions";
  const conversionBoostDesc =
    "AI-optimized visuals that increase click-through rates";
  const engagementRate = "engagement rate";
  const testimonialShort =
    '"We now create stunning product visuals in minutes with AI. Our conversion rates increased by 32%!"';

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const shop = searchParams.get("shop");

    if (shop) {
      document.cookie = `shop=${shop};path=/;domain=${window.location.hostname};SameSite=Lax;secure`;
    }
  }, [location]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-green-50 overflow-x-hidden">
      <div className="min-h-screen w-full flex items-center justify-center py-6 md:py-12">
        <div
          className={`w-full ${
            twoColumn ? "max-w-6xl" : "max-w-xl"
          } mx-auto px-4 md:px-8`}
        >
          {/* Desktop Two-Column Layout */}
          <div
            className={`grid ${
              twoColumn ? "md:grid-cols-2" : "md:grid-cols-1"
            } gap-16 items-center`}
          >
            {/* Value Props Section - Hidden on Mobile */}
            {valueProps && (
              <div className="hidden md:block md:pt-12">{valueProps}</div>
            )}

            {/* Mobile Marketing Messages - Shown before Sign-up form */}
            <div className="md:hidden space-y-6 mb-8">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 text-center mb-3">
                  {transformImages}
                </h2>
                <p className="text-base text-gray-600 text-center mb-6">
                  {joinMerchants}
                </p>

                {/* Mobile Metrics Bar */}
                <div className="flex justify-center gap-8 mb-6">
                  <div className="text-center">
                    <div className="text-xl font-bold text-purple-600">
                      +32%
                    </div>
                    <div className="text-xs text-gray-600">
                      {"Conversion Growth"}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-xl font-bold text-purple-600">
                      +76%
                    </div>
                    <div className="text-xs text-gray-600">
                      {engagementRate}
                    </div>
                  </div>
                </div>
              </div>

              {/* Feature Highlights */}
              <div className="space-y-4 px-2">
                <div className="flex items-center gap-3 p-4 bg-white/80 backdrop-blur-sm rounded-lg">
                  <Image className="w-5 h-5 text-purple-500 shrink-0" />
                  <div className="text-sm text-gray-900">{aiImageGenDesc}</div>
                </div>
                <div className="flex items-center gap-3 p-4 bg-white/80 backdrop-blur-sm rounded-lg">
                  <Zap className="w-5 h-5 text-purple-500 shrink-0" />
                  <div className="text-sm text-gray-900">
                    {conversionBoostDesc}
                  </div>
                </div>
              </div>

              {/* Testimonial */}
              <div className="px-2">
                <div className="p-4 bg-white/80 backdrop-blur-sm rounded-lg">
                  <p className="text-sm text-gray-700 italic mb-4">
                    {testimonialShort}
                  </p>
                  <div className="flex items-center gap-3">
                    <img
                      src="https://www.marketplaceiq.io/wp-content/uploads/2024/11/yasemin.webp"
                      alt={"Yasemin Gülle"}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                    <div>
                      <p className="text-sm font-semibold text-gray-900">
                        {"Yasemin Gülle"}
                      </p>
                      <p className="text-xs text-gray-600">
                        {"Sr. Digital Channels Manager"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Sign Up Card */}
            <div>
              <div className="bg-white py-8 px-6 md:px-8 shadow-xl ring-1 ring-gray-900/10 rounded-lg">
                <div className="flex justify-start mb-6">
                  <img
                    src="https://www.marketplaceiq.io/wp-content/uploads/2024/12/black2.png"
                    className="h-6 w-auto"
                  />
                </div>
                <div>{children}</div>
              </div>

              <div className="mt-6 text-center text-sm text-gray-600">
                {"By signing up, you agree to our"}
                <a
                  href="https://www.marketplaceiq.io/home/terms-of-use/"
                  className="text-purple-600 hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {"terms"}
                </a>{" "}
                {"and"}
                <a
                  href="https://www.marketplaceiq.io/privacy-policy/"
                  className="text-purple-600 hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {"privacy policy"}
                </a>
                .
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
